import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { requireAuth } from "~/lib/auth-guard";
import { useToast } from "@alchema/ui";
import type { InterviewQuestion } from "@alchema/shared";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import * as m from "~/paraglide/messages";
import { SwarmProgressPanel } from "~/components/swarm/SwarmProgressPanel";
import { startSwarmProgress } from "~/lib/swarm/progress-bridge";
import { useSwarmProgressStore } from "~/lib/stores/swarm-progress";

export const Route = createFileRoute("/interview-prep/question-bank")({
  beforeLoad: async () => { await requireAuth(); },
  component: QuestionBankPage,
});

const SENIORITY_LEVELS = [
  { value: "entry" as const, labelKey: "interview_qbank_seniority_entry" as const },
  { value: "mid" as const, labelKey: "interview_qbank_seniority_mid" as const },
  { value: "senior" as const, labelKey: "interview_qbank_seniority_senior" as const },
  { value: "executive" as const, labelKey: "interview_qbank_seniority_executive" as const },
];

const SENIORITY_LABELS: Record<string, () => string> = {
  interview_qbank_seniority_entry: () => m.interview_qbank_seniority_entry(),
  interview_qbank_seniority_mid: () => m.interview_qbank_seniority_mid(),
  interview_qbank_seniority_senior: () => m.interview_qbank_seniority_senior(),
  interview_qbank_seniority_executive: () => m.interview_qbank_seniority_executive(),
};

const CATEGORY_DOT_COLORS: Record<string, string> = {
  behavioral: "var(--accent)",
  technical: "var(--status-purple, #a78bfa)",
  situational: "var(--warning)",
  "company-specific": "var(--success)",
};

const DIFFICULTY_DOT_COLORS: Record<string, string> = {
  easy: "var(--success)",
  medium: "var(--warning)",
  hard: "var(--error)",
};

function CategoryDot({ category }: { category: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs whitespace-nowrap" style={{ color: "var(--text-secondary)" }}>
      <span
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: CATEGORY_DOT_COLORS[category] ?? "var(--text-muted)" }}
      />
      <span className="capitalize">{category}</span>
    </span>
  );
}

function DifficultyDot({ difficulty }: { difficulty: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
      <span
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: DIFFICULTY_DOT_COLORS[difficulty] ?? "var(--text-muted)" }}
      />
      <span className="capitalize">{difficulty}</span>
    </span>
  );
}

export function QuestionBankGeneratedResultsToolbar({
  filteredCount,
  totalCount,
  categories,
  difficulties,
  filterCategory,
  filterDifficulty,
  setFilterCategory,
  setFilterDifficulty,
}: {
  filteredCount: number;
  totalCount: number;
  categories: string[];
  difficulties: string[];
  filterCategory: string;
  filterDifficulty: string;
  setFilterCategory: (category: string) => void;
  setFilterDifficulty: (difficulty: string) => void;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
        {filteredCount} / {totalCount}
      </span>
      <AiGeneratedBadge />
      <div className="flex gap-1 flex-wrap">
        <button
          onClick={() => setFilterCategory("all")}
          className="rounded-md px-2.5 py-1 text-xs font-medium transition-all duration-150"
          style={{
            background: filterCategory === "all" ? "var(--surface)" : "transparent",
            color: filterCategory === "all" ? "var(--text)" : "var(--text-muted)",
            border: filterCategory === "all" ? "1px solid var(--border)" : "1px solid transparent",
          }}
        >
          All categories
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat === filterCategory ? "all" : cat)}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all duration-150"
            style={{
              background: filterCategory === cat ? "var(--surface)" : "transparent",
              color: filterCategory === cat ? "var(--text)" : "var(--text-muted)",
              border: filterCategory === cat ? "1px solid var(--border)" : "1px solid transparent",
            }}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
              style={{ background: CATEGORY_DOT_COLORS[cat] ?? "var(--text-muted)" }}
            />
            <span className="capitalize">{cat}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        {difficulties.map((diff) => (
          <button
            key={diff}
            onClick={() => setFilterDifficulty(diff === filterDifficulty ? "all" : diff)}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all duration-150"
            style={{
              background: filterDifficulty === diff ? "var(--surface)" : "transparent",
              color: filterDifficulty === diff ? "var(--text)" : "var(--text-muted)",
              border: filterDifficulty === diff ? "1px solid var(--border)" : "1px solid transparent",
            }}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
              style={{ background: DIFFICULTY_DOT_COLORS[diff] ?? "var(--text-muted)" }}
            />
            <span className="capitalize">{diff}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuestionBankPage() {
  const { toast } = useToast();
  const [roleType, setRoleType] = React.useState("");
  const [seniority, setSeniority] = React.useState<"entry" | "mid" | "senior" | "executive">("mid");
  const [questions, setQuestions] = React.useState<InterviewQuestion[]>([]);
  const [filterCategory, setFilterCategory] = React.useState<string>("all");
  const [filterDifficulty, setFilterDifficulty] = React.useState<string>("all");
  const progressRef = React.useRef<ReturnType<typeof startSwarmProgress> | null>(null);

  React.useEffect(() => {
    return () => { useSwarmProgressStore.getState().reset(); };
  }, []);

  const generateMutation = useMutation(
    orpc.interview.questionBank.mutationOptions({
      onSuccess: (data) => {
        progressRef.current?.complete();
        setQuestions(data.questions);
        setFilterCategory("all");
        setFilterDifficulty("all");
        toast(m.interview_qbank_toast_generated(), "success");
      },
      onError: (err) => {
        progressRef.current?.fail();
        toast(err.message || m.interview_qbank_toast_generate_error(), "error");
      },
    }),
  );

  const handleGenerate = () => {
    if (!roleType.trim()) {
      toast(m.interview_qbank_toast_enter_role(), "error");
      return;
    }
    progressRef.current = startSwarmProgress('career-coaching');
    generateMutation.mutate({ roleType: roleType.trim(), seniorityLevel: seniority });
  };

  // Derived filter values
  const categories = React.useMemo(() => {
    return Array.from(new Set(questions.map((q) => q.category)));
  }, [questions]);

  const difficulties = React.useMemo(() => {
    return Array.from(new Set(questions.map((q) => q.difficulty)));
  }, [questions]);

  const filteredQuestions = React.useMemo(() => {
    return questions.filter((q) => {
      if (filterCategory !== "all" && q.category !== filterCategory) return false;
      if (filterDifficulty !== "all" && q.difficulty !== filterDifficulty) return false;
      return true;
    });
  }, [questions, filterCategory, filterDifficulty]);

  return (
    <div className="space-y-6">
      {/* Swarm Progress */}
      <SwarmProgressPanel />

      {/* Controls */}
      <div
        className="rounded-xl p-6"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>{m.interview_qbank_title()}</h2>
        <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
          {m.interview_qbank_subtitle()}
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
              {m.interview_qbank_label_role()}
            </label>
            <input
              type="text"
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleGenerate(); }}
              placeholder={m.interview_qbank_role_placeholder()}
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1"
              style={{
                border: "1px solid var(--border)",
                background: "var(--bg-secondary)",
                color: "var(--text)",
              }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-2" style={{ color: "var(--text-secondary)" }}>
              {m.interview_qbank_label_seniority()}
            </label>
            <div
              className="flex gap-0.5 rounded-lg p-0.5 w-fit"
              style={{ background: "var(--surface-raised)" }}
            >
              {SENIORITY_LEVELS.map((level) => (
                <button
                  key={level.value}
                  onClick={() => setSeniority(level.value)}
                  className="rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150"
                  style={{
                    background: seniority === level.value ? "var(--surface)" : "transparent",
                    color: seniority === level.value ? "var(--text)" : "var(--text-muted)",
                    boxShadow: seniority === level.value ? "var(--shadow-sm)" : "none",
                  }}
                >
                  {SENIORITY_LABELS[level.labelKey]()}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={!roleType.trim() || generateMutation.isPending}
            className="btn btn-primary btn-sm"
          >
            {generateMutation.isPending ? (
              <span className="flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {m.interview_qbank_generating()}
              </span>
            ) : (
              m.interview_qbank_generate_cta()
            )}
          </button>
        </div>
      </div>

      {/* Generated Questions Table */}
      {questions.length > 0 && (
        <div className="animate-fade-up">
          {/* Filter bar */}
          <QuestionBankGeneratedResultsToolbar
            filteredCount={filteredQuestions.length}
            totalCount={questions.length}
            categories={categories}
            difficulties={difficulties}
            filterCategory={filterCategory}
            filterDifficulty={filterDifficulty}
            setFilterCategory={setFilterCategory}
            setFilterDifficulty={setFilterDifficulty}
          />

          {/* Table */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--border)" }}
          >
            {/* Header */}
            <div
              className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5"
              style={{
                borderBottom: "1px solid var(--border)",
                background: "var(--surface-raised)",
              }}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                Question
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                Category
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                Difficulty
              </span>
            </div>

            {/* Rows */}
            <div style={{ background: "var(--surface)" }}>
              {filteredQuestions.map((q, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 items-center px-4 py-3 transition-colors"
                  style={{
                    borderBottom: idx < filteredQuestions.length - 1 ? "1px solid var(--border)" : "none",
                  }}
                >
                  <p className="text-sm leading-snug" style={{ color: "var(--text)" }}>
                    {q.question}
                  </p>
                  <CategoryDot category={q.category} />
                  <DifficultyDot difficulty={q.difficulty} />
                </div>
              ))}
              {filteredQuestions.length === 0 && (
                <div className="py-10 text-center">
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>No questions match the selected filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
